import React, { useEffect, useState } from 'react';
import learn41Logo from '@/assets/images/learn41.png';
import { isLearn41Domain } from '@/auth/useTenant';
import { captureEvent } from '@/posthog-analytics/posthog';

const TENANT_LOGO_BASE = 'https://storage.googleapis.com/recruitbot-public/tenant-logos';

/**
 * Where a tenant's logo lives. The object name is the lookup key, so a domain
 * with no uploaded logo resolves to a URL that 404s — callers must render on
 * error rather than checking existence up front.
 */
export function getTenantLogoUrl(domain: string): string {
  return `${TENANT_LOGO_BASE}/${domain}.png`;
}

interface TenantLogoProps {
  /** Tenant domain (e.g., "think41", "demo") */
  domain: string;
  /** Tenant display name (used for fallback avatar and text) */
  name: string;
  /** CSS classes for the logo image */
  className?: string;
  /** CSS classes for the fallback avatar circle */
  avatarClassName?: string;
  /** CSS classes for the tenant name text */
  nameClassName?: string;
  /** Show tenant name alongside logo (default: false) */
  showNameWithLogo?: boolean;
  /** Compact mode: show only primary logo without co-branding (default: false) */
  compact?: boolean;
  /** Replaces the default avatar + name when the tenant has no logo */
  fallback?: React.ReactNode;
}

/**
 * TenantLogo Component
 *
 * Displays a tenant's logo from the public assets bucket, falling back to an
 * avatar with the first letter plus the tenant name when none is uploaded.
 */
export const TenantLogo: React.FC<TenantLogoProps> = ({
  domain,
  name,
  className = 'h-8 sm:h-9 md:h-10 max-w-full',
  avatarClassName = 'h-10 w-10 rounded-full bg-primary flex items-center justify-center text-white font-bold text-xl',
  nameClassName = 'text-xl font-bold text-text-primary group-hover:text-primary transition-colors',
  showNameWithLogo = false,
  compact = false,
  fallback,
}) => {
  const [missing, setMissing] = useState(false);
  const isLearn41 = isLearn41Domain();
  const tenantLogoUrl = missing ? null : getTenantLogoUrl(domain);

  // A new tenant deserves a fresh attempt; without this a resolved miss would
  // stick across domain changes.
  useEffect(() => setMissing(false), [domain]);

  const handleMissing = () => {
    setMissing(true);
    captureEvent('tenant_logo_load_failed', { tenant_domain: domain });
  };

  // Learn41 theme: Learn41 logo alongside the tenant's own
  if (isLearn41) {
    return (
      <>
        <img src={learn41Logo} alt="Learn41 Logo" className={`${className} object-contain`} />
        {!compact && tenantLogoUrl && (
          <>
            <span className="mx-2 text-border-primary text-lg font-light">×</span>
            <img
              src={tenantLogoUrl}
              alt={`${name} Logo`}
              className={`${className} object-contain`}
              onError={handleMissing}
            />
          </>
        )}
        {!compact && showNameWithLogo && !tenantLogoUrl && <h1 className={nameClassName}>{name}</h1>}
      </>
    );
  }

  if (tenantLogoUrl) {
    return (
      <>
        <img src={tenantLogoUrl} alt={`${name} Logo`} className={className} onError={handleMissing} />
        {showNameWithLogo && <h1 className={nameClassName}>{name}</h1>}
      </>
    );
  }

  if (fallback) {
    return <>{fallback}</>;
  }

  const avatarLetter = name.charAt(0).toUpperCase();

  return (
    <>
      <div className={avatarClassName}>{avatarLetter}</div>
      <h1 className={nameClassName}>{name}</h1>
    </>
  );
};
